"use client";

import { useEffect, useRef, useState } from "react";
import { Highlight, themes } from "prism-react-renderer";
import { Card } from "@heroui/react";
import { useTheme } from "@/components/theme-provider";
import { ArrowRight } from "@/components/icons";

export interface LookupResponseItem {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  responseKeys: string[];
}

export function LookupResponseExplorer({
  items,
  response,
  scrollSteps = false,
}: {
  items: LookupResponseItem[];
  response: Record<string, unknown>;
  scrollSteps?: boolean;
}) {
  const [selectedTitle, setSelectedTitle] = useState(items[0]?.title ?? "");
  const stageRef = useRef<HTMLDivElement>(null);
  const selected = items.find((item) => item.title === selectedTitle) ?? items[0];
  const { resolvedTheme } = useTheme();
  const code = JSON.stringify(response, null, 2);
  const codeTheme =
    resolvedTheme === "light"
      ? themes.oneLight
      : {
          ...themes.nightOwl,
          plain: { ...themes.nightOwl.plain, backgroundColor: "transparent" },
        };

  useEffect(() => {
    if (!scrollSteps) return;

    const stage = stageRef.current;
    if (!stage) return;

    let frame = 0;
    const updateSelection = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const stageTop = stage.getBoundingClientRect().top + window.scrollY;
        const step = Math.floor((window.scrollY - stageTop) / (window.innerHeight * 0.7));
        const item = items[Math.min(items.length - 1, Math.max(0, step))];
        if (item) setSelectedTitle(item.title);
      });
    };

    window.addEventListener("scroll", updateSelection, { passive: true });
    window.addEventListener("resize", updateSelection);
    updateSelection();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", updateSelection);
      window.removeEventListener("resize", updateSelection);
    };
  }, [items, scrollSteps]);

  if (!selected) return null;

  return (
    <div ref={stageRef} className={`min-w-0 ${scrollSteps ? "relative lg:min-h-[420vh]" : ""}`}>
      <p className="mb-3 text-sm text-text-tertiary">Select a detail to explore the response</p>
      <div className={`grid min-w-0 items-stretch gap-4 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] ${scrollSteps ? "lg:sticky lg:top-24" : ""}`}>
        <div className="min-w-0">
          <div role="group" aria-label="Response details" className="grid gap-2">
            {items.map((item) => {
              const active = item.title === selected.title;
              return (
                <button
                  key={item.title}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setSelectedTitle(item.title)}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                    active
                      ? "border-brand bg-brand/5"
                      : "border-border-secondary bg-sidebar hover:border-brand/40 dark:bg-card"
                  }`}
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
                    <item.icon className="size-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-text-primary">{item.title}</span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-text-tertiary line-clamp-2">
                      {item.desc}
                    </span>
                  </span>
                  <ArrowRight
                    className={`size-4 shrink-0 transition-colors ${
                      active ? "text-brand" : "text-text-quaternary"
                    }`}
                  />
                </button>
              );
            })}
          </div>
        </div>

        <Card
          variant="default"
          className="flex min-w-0 flex-col gap-0 overflow-hidden rounded-[20px] border border-border-secondary bg-white p-0 shadow-none dark:bg-[#0f1011] lg:h-full lg:min-h-0"
        >
        <div className="flex items-center justify-between border-b border-border-secondary px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-text-primary">Example response</p>
            <p className="mt-0.5 text-xs text-text-tertiary">{selected.title}</p>
          </div>
          <span className="font-mono text-3xs uppercase tracking-wider text-text-quaternary">JSON</span>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          <Highlight theme={codeTheme} code={code} language="json">
            {({ tokens, getLineProps, getTokenProps }) => (
              <pre className="max-h-[32rem] overflow-auto py-3 font-mono text-xs leading-[1.7] lg:h-full lg:max-h-none">
                <code className="block min-w-max">
                  {tokens.map((line, lineIndex) => {
                    const content = line.map((token) => token.content).join("");
                    const highlighted = selected.responseKeys.some((key) => content.includes(`\"${key}\"`));
                    const lineProps = getLineProps({ line });
                    return (
                      <div
                        key={lineIndex}
                        {...lineProps}
                        className={`flex min-w-full border-l-2 px-4 ${
                          highlighted ? "border-brand bg-brand/10" : "border-transparent"
                        }`}
                      >
                        <span className="mr-4 w-7 shrink-0 select-none text-right text-text-quaternary">
                          {lineIndex + 1}
                        </span>
                        <span className="whitespace-pre">
                          {line.map((token, tokenIndex) => (
                            <span key={tokenIndex} {...getTokenProps({ token })} />
                          ))}
                        </span>
                      </div>
                    );
                  })}
                </code>
              </pre>
            )}
          </Highlight>
        </div>
        <div className="border-t border-border-secondary px-5 py-3 text-xs text-text-quaternary">
          Illustrative schema and sample values
        </div>
        </Card>
      </div>
    </div>
  );
}
